import React, { useState, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { TbMessageCircleDollar } from 'react-icons/tb';
import StorefrontListButtony from '../../Storefront/Products/StorefrontListButtony';
import getImageUrlUtil from '../../../../utils/getImageUrl';
import CreatePriceStr from '../../../common/common/CreatePriceStr';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getBackendUrl } from '../../../../utils/getBackendUrl';
const paths = require('../../../../paths.json');

const LeadCapture = React.memo((props) => {
  const { t } = useTranslation();
  const [leadCaptures, setLeadCaptures] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load lead captures - try multiple sources: influencer_content_list (from tab change API), activeProductData (for creator view), then fetch from API (for user view)
  useEffect(() => {
    // First, check if influencer_content_list was passed from tab change API response (for sign_up tab, this contains lead captures)
    if (props.selected_tab === 'sign_up' && props.influencer_content_list && Array.isArray(props.influencer_content_list) && props.influencer_content_list.length > 0) {
      setLeadCaptures(props.influencer_content_list);
      setLoading(false);
      return;
    }
    
    // Also check if influencer_content_list is empty array (from API but no items)
    if (props.selected_tab === 'sign_up' && props.influencer_content_list && Array.isArray(props.influencer_content_list) && props.influencer_content_list.length === 0) {
      setLeadCaptures([]);
      setLoading(false);
      return;
    }
    
    // Second, try activeProductData (for creator view/preview)
    const leadCaptureData = props.activeProductData?.active_products?.[0]?.lead_capture;
    
    if (leadCaptureData) {
      // Check if active flag exists and is true, or if list exists (treat undefined as active)
      const isActive = leadCaptureData.active === true || leadCaptureData.active === undefined;
      const leadCaptureList = leadCaptureData.list || [];
      
      if (isActive && leadCaptureList.length > 0) {
        setLeadCaptures(leadCaptureList);
        setLoading(false);
        return;
      }
    }
    
    // If not in activeProductData or props, fetch from storefront API (for user view)
    const influencerStr = props.influencer_info?.influencer || props.influencer_info?.username || props.influencer_str || '';
    if (!influencerStr) {
      setLeadCaptures([]);
      setLoading(false);
      return;
    }

    // Resolve backend URL
    const allowedHostnames = [
      'localhost',
      'localhost:3000',
      'fitwith.io',
      'withme.so',
      'with-me.shop',
      'with-me.store'
    ];
    const currentHostname = window.location.hostname;
    let backend_url = paths.BACKEND_URL;
    if (!allowedHostnames.includes(currentHostname.replace(/^www\./, ''))) {
      backend_url = getBackendUrl();
    }

    const form_data = new FormData();
    form_data.append("currency", props.currency || 'usd');

    axios({
      method: "post",
      url: `${backend_url}api/influencer/storefront_initial_data/${influencerStr}`,
      data: form_data,
      headers: {
        "Content-type": "application/json",
      },
      withCredentials: true,
    }).then((res) => {
      const leadCapturesFromAPI = res.data.data?.lead_captures || [];
      setLeadCaptures(leadCapturesFromAPI);
      setLoading(false);
    }).catch((err) => {
      console.log('Error fetching lead captures:', err);
      setLeadCaptures([]);
      setLoading(false);
    });
  }, [props.selected_tab, props.influencer_content_list, props.activeProductData, props.influencer_info, props.influencer_str, props.currency]);

  // For app view (sign up tab), always use buttony layout
  
  // Get window width with fallback
  const windowWidth = props.windowWidth || (typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Helper functions (same as Storefront)
  const getImageUrl = (item) => {
    const candidate = item?.thumbnail || item?.product_thumbnail || item?.image || item?.image_url || '';
    if (!candidate || candidate === 'null/' || candidate === 'null') {
      return null;
    }
    return getImageUrlUtil(candidate);
  };

  const getDescription = (item) => {
    return item?.description || item?.subtitle || item?.bio || "";
  };

  const getPriceStr = (item, productType = null) => {
    // Lead captures are always free
    return null;
  };

  const getSeparatePrices = (item, productType = null) => {
    // Lead captures are always free
    return { monthlyPrice: null, annualPrice: null };
  };

  const getCtaLabel = (productType, item = null) => {
    if (productType === 'lead_capture') {
      return item?.button_text || 'Submit';
    }
    return 'Submit';
  };

  const getBrandColor = () => {
    // Get brand color, with fallback to gold (#FDB62B) instead of CSS variable
    const customBrandColor = props.designProperties?.brandColorUpdated || props.designProperties?.brandColor;
    if (customBrandColor && customBrandColor !== '' && customBrandColor !== null) {
      return customBrandColor;
    }
    // Check theme brand color as secondary fallback
    if (props.theme?.brandColor && props.theme.brandColor !== '' && props.theme.brandColor !== null) {
      return props.theme.brandColor;
    }
    // Fallback to gold color instead of CSS variable to ensure it works in all contexts
    return '#FDB62B';
  };

  const getItemSurfaceColors = () => {
    if (props.theme) {
      const secondaryText = props.theme.mixWith 
        ? props.theme.mixWith(props.theme.appText, props.theme.appBg, 0.4)
        : '#6b7280';
        
      return {
        cardBg: props.theme.cardBg,
        cardBorder: props.theme.cardBorder,
        cardBgHover: props.theme.cardBgHover,
        hoverShadow: props.theme.hoverShadow,
        appText: props.theme.appText,
        secondaryText: secondaryText,
        brandColor: props.theme.brandColor,
        itemBgMode: props.designProperties?.itemBgModeUpdated || props.designProperties?.itemBgMode || 'subtle',
        cardTextColor: props.designProperties?.cardTextColorUpdated || props.designProperties?.cardTextColor,
        cardBg: (props.designProperties?.itemBgModeUpdated || props.designProperties?.itemBgMode || 'subtle') === 'custom' 
          ? (props.designProperties?.itemCardBgColorUpdated || props.designProperties?.itemCardBgColor || props.theme.cardBg)
          : props.theme.cardBg,
      };
    }
    
    return {
      cardBg: '#FFFFFF',
      cardBorder: '#E5E7EB',
      cardBgHover: '#F9FAFB',
      hoverShadow: '0 2px 6px 0 rgba(16, 24, 40, 0.02)',
      appText: '#111827',
      secondaryText: '#6b7280',
      brandColor: getBrandColor(),
      itemBgMode: props.designProperties?.itemBgModeUpdated || props.designProperties?.itemBgMode || 'subtle',
      cardTextColor: props.designProperties?.cardTextColorUpdated || props.designProperties?.cardTextColor,
      cardBg: (props.designProperties?.itemBgModeUpdated || props.designProperties?.itemBgMode || 'subtle') === 'custom' 
        ? (props.designProperties?.itemCardBgColorUpdated || props.designProperties?.itemCardBgColor || '#FFFFFF')
        : '#FFFFFF',
    };
  };

  const getTruncatedPlainText = (htmlString, maxChars = 30) => {
    if (!htmlString) return "";
    const plain = htmlString.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (plain.length <= maxChars) return plain;
    return plain.slice(0, maxChars).trim() + "…";
  };

  const getTruncatedHtml = (htmlString, maxChars = 120) => {
    if (!htmlString || typeof htmlString !== 'string') return "";
    const plain = htmlString.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (plain.length <= maxChars) return htmlString.trim();
    
    let textLength = 0;
    let result = '';
    let inTag = false;
    let tagBuffer = '';
    
    for (let i = 0; i < htmlString.length; i++) {
      const char = htmlString[i];
      
      if (char === '<') {
        inTag = true;
        tagBuffer = char;
      } else if (char === '>') {
        inTag = false;
        tagBuffer += char;
        result += tagBuffer;
        tagBuffer = '';
      } else if (inTag) {
        tagBuffer += char;
      } else {
        if (textLength < maxChars) {
          result += char;
          textLength++;
        } else {
          result += '...';
          break;
        }
      }
    }
    
    return result;
  };

  // Filter hidden items
  const visibleLeadCaptures = useMemo(() => {
    return (leadCaptures || []).filter(item => item?.hidden !== true);
  }, [leadCaptures]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!visibleLeadCaptures || visibleLeadCaptures.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <TbMessageCircleDollar size={48} color="#ccc" style={{ margin: '0 auto 16px' }} />
        <Box sx={{ color: '#6b7280', fontSize: '18px', fontWeight: 600, mb: 1 }}>
          {t('influencer.creatorView.noSignUpForms')}
        </Box>
        <Box sx={{ color: '#9ca3af', fontSize: '14px' }}>
          {t('influencer.creatorView.checkBackSignUpForms')}
        </Box>
      </Box>
    );
  }

  const surface = getItemSurfaceColors();

  // Always render buttony layout for app view (sign up tab) - same as storefront
  return (
    <Box sx={{ 
      padding: { xs: "0px 14px 60px 14px", lg: "0px 24px 60px 24px" },
      maxWidth: 560, 
      margin: '0 auto'
    }}>
      <StorefrontListButtony
        items={visibleLeadCaptures}
        windowWidth={windowWidth}
        productType="lead_capture"
        getImageUrl={getImageUrl}
        getPriceStr={(item) => getPriceStr(item, 'lead_capture')}
        getSeparatePrices={(item) => getSeparatePrices(item, 'lead_capture')}
        getDescription={getDescription}
        getCtaLabel={(productType, item) => getCtaLabel(productType, item)}
        getBrandColor={getBrandColor}
        handleOpenItem={() => {}} // Lead captures don't open items
        getTruncatedPlainText={getTruncatedPlainText}
        getTruncatedHtml={getTruncatedHtml}
        surface={surface}
        mobilePreview={false}
        influencer_info={props.influencer_info}
        current_page_view={props.current_page_view}
        async_tier_apply={false}
        isLoggedOutView={props.isLoggedOutView || false}
        showLeadCaptureSnackbar={props.showLeadCaptureSnackbar}
      />
    </Box>
  );
});

export default LeadCapture;
